// src/pages/evaluation-system/UserValidation.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../UserValidation.css";

const API = String(process.env.REACT_APP_API || "").replace(/\/$/, "");

const onlyDigits = (value = "", max = 99) => String(value || "").replace(/\D/g, "").slice(0, max);
const normalizeToken = (value = "") => String(value || "").trim().replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 80);

const getValidationTokenFromUrl = (params) => {
    const queryValidationToken = params.get("validationToken") || params.get("publicValidationToken") || "";
    const queryToken = params.get("token") || ""; // compatibilidad con QR antiguos
    const parts = String(window.location.pathname || "").split("/").filter(Boolean);
    const pathToken = parts[0] === "user-validation" && parts[1] ? decodeURIComponent(parts[1]) : "";

    // Soporta:
    // /user-validation/:validationToken
    // /user-validation?publicValidation=1&validationToken=...
    // /?publicValidation=1&validationToken=...
    // /user-validation?token=... QR antiguos
    return normalizeToken(pathToken || queryValidationToken || queryToken);
};

const getPublicJson = async (url, options = {}) => {
    const response = await fetch(url, {
        ...options,
        credentials: "omit",
        cache: "no-store",
        headers: {
            Accept: "application/json",
            ...(options.body ? { "Content-Type": "application/json" } : {}),
            ...(options.headers || {}),
        },
    });

    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        payload = null;
    }

    if (!response.ok) {
        const error = new Error(payload?.msg || `Error HTTP ${response.status}`);
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    return payload;
};

const dataUrlFromCanvas = (video, maxWidth = 1280) => {
    const canvas = document.createElement("canvas");
    const ratio = video.videoWidth > maxWidth ? maxWidth / video.videoWidth : 1;
    canvas.width = Math.max(1, Math.round(video.videoWidth * ratio));
    canvas.height = Math.max(1, Math.round(video.videoHeight * ratio));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.86);
};

export default function UserValidation() {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const token = useMemo(() => getValidationTokenFromUrl(params), [params]);
    const [clientData, setClientData] = useState(() => ({
        dni: onlyDigits(params.get("dni") || "", 8),
        phone: onlyDigits(params.get("phone") || params.get("phoneNumber") || "", 9),
        fullName: "",
    }));

    const dni = clientData.dni;
    const phone = clientData.phone;

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);

    const [cameraReady, setCameraReady] = useState(false);
    const [cameraMode, setCameraMode] = useState("environment");
    const [step, setStep] = useState("front");
    const [front, setFront] = useState("");
    const [back, setBack] = useState("");
    const [faceVideo, setFaceVideo] = useState("");
    const [recording, setRecording] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [done, setDone] = useState(false);
    const [locked, setLocked] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(true);

    const stopCamera = useCallback(() => {
        streamRef.current?.getTracks?.().forEach((track) => track.stop());
        streamRef.current = null;
        setCameraReady(false);
    }, []);

    const loadSession = useCallback(async () => {
        if (!token) {
            setSessionLoading(false);
            return;
        }

        try {
            setSessionLoading(true);
            const cacheBust = Date.now();
            const data = await getPublicJson(`${API}/credit-validation/public/${encodeURIComponent(token)}?_=${cacheBust}`);
            const session = data?.data || {};
            const sessionDni = onlyDigits(session.dni || "", 8);
            const sessionPhone = onlyDigits(session.phoneNumber || session.phone || "", 9);

            // La URL puede venir solo con token. El DNI/celular se cargan desde la sesión pública del backend.
            // Si la URL trae DNI/celular, se validan contra la sesión para evitar enlaces manipulados.
            if (dni && sessionDni && String(sessionDni) !== String(dni)) {
                setLocked(true);
                setDone(true);
                setMessage("El DNI del enlace no coincide con la sesión de validación. Solicite un nuevo QR al asesor.");
                stopCamera();
                return;
            }

            if (phone && sessionPhone && String(sessionPhone) !== String(phone)) {
                setLocked(true);
                setDone(true);
                setMessage("El celular del enlace no coincide con la sesión de validación. Solicite un nuevo QR al asesor.");
                stopCamera();
                return;
            }

            setClientData({
                dni: sessionDni || dni,
                phone: sessionPhone || phone,
                fullName: session.fullName || "",
            });

            // Regla definitiva: solo se bloquea cuando el backend confirma evidencias reales.
            if (session.evidenceComplete === true) {
                setLocked(true);
                setDone(true);
                setMessage(session.message || "Verificación facial completada.");
                stopCamera();
                return;
            }

            setLocked(false);
            setDone(false);
            setMessage(session.message || "Validación pendiente. Complete el proceso de identidad.");
        } catch (err) {
            setLocked(true);
            setDone(true);
            setMessage(err?.payload?.msg || err?.message || "No se pudo validar el enlace. Solicite un nuevo QR al asesor.");
            stopCamera();
        } finally {
            setSessionLoading(false);
        }
    }, [token, dni, phone, stopCamera]);

    const startCamera = useCallback(async (mode = cameraMode) => {
        try {
            stopCamera();
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: step === "face",
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setCameraReady(true);
            setMessage("");
        } catch (err) {
            setMessage("No se pudo abrir la cámara. Revise permisos del navegador y use HTTPS.");
        }
    }, [cameraMode, step, stopCamera]);

    useEffect(() => {
        loadSession();
    }, [loadSession]);

    useEffect(() => {
        if (sessionLoading || locked || done) return undefined;
        startCamera(step === "face" ? "user" : "environment");
        return stopCamera;
    }, [step, sessionLoading, locked, done, startCamera, stopCamera]);

    const switchCamera = async () => {
        const next = cameraMode === "environment" ? "user" : "environment";
        setCameraMode(next);
        await startCamera(next);
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        if (!video || !cameraReady) return;
        const image = dataUrlFromCanvas(video);
        if (step === "front") {
            setFront(image);
            setStep("back");
        } else if (step === "back") {
            setBack(image);
            setStep("face");
        }
    };

    const startFaceRecording = async () => {
        const stream = streamRef.current;
        if (!stream || recording) return;
        chunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
        const recorder = new MediaRecorder(stream, { mimeType });
        recorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
        };

        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: "video/webm" });
            const reader = new FileReader();
            reader.onloadend = () => {
                setFaceVideo(String(reader.result || ""));
                setRecording(false);
                setCountdown(5);
                setMessage("Video facial grabado correctamente.");
            };
            reader.readAsDataURL(blob);
        };

        setRecording(true);
        setCountdown(5);
        recorder.start(250);

        let left = 5;
        const interval = setInterval(() => {
            left -= 1;
            setCountdown(left);
            if (left <= 0) clearInterval(interval);
        }, 1000);

        setTimeout(() => {
            if (recorder.state !== "inactive") recorder.stop();
        }, 5000);
    };

    const submitValidation = async () => {
        if (!front || !back || !faceVideo) {
            setMessage("Complete foto frontal, foto posterior y video facial antes de enviar.");
            return;
        }
        try {
            setLoading(true);
            const data = await getPublicJson(`${API}/credit-validation/submit`, {
                method: "POST",
                body: JSON.stringify({
                    token,
                    dni,
                    phoneNumber: phone,
                    dniFront: front,
                    dniBack: back,
                    faceVideo,
                }),
            });

            if (data?.msg !== "success") throw new Error(data?.msg || "No se pudo guardar la validación.");
            setDone(true);
            setLocked(false);
            setMessage("Validación enviada correctamente. Ya puede regresar con el asesor.");
            stopCamera();
        } catch (err) {
            const code = err?.payload?.code;
            const msg = err?.payload?.msg || err?.message || "No se pudo enviar la validación.";
            if (code === "VALIDATION_ALREADY_COMPLETED") {
                setLocked(true);
                setDone(true);
                stopCamera();
            }
            setMessage(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return <main className="uvPage"><section className="uvCard"><h1>Enlace inválido</h1><p>Falta el token de validación. Genere un nuevo QR desde CreditFlow.</p></section></main>;
    }

    if (sessionLoading) {
        return <main className="uvPage"><section className="uvCard"><h1>Validando enlace...</h1><p>Espere un momento.</p></section></main>;
    }

    if (!dni || !phone) {
        return <main className="uvPage"><section className="uvCard"><h1>Enlace inválido</h1><p>No se pudo cargar DNI y celular desde la sesión. Genere un nuevo QR desde CreditFlow.</p></section></main>;
    }

    return (
        <main className="uvPage">
            <section className="uvCard">
                <div className="uvHeader">
                    <span>MovilesPay</span>
                    <h1>Validación de identidad</h1>
                    <p>DNI {dni} · Celular {phone}</p>
                </div>

                {locked && (
                    <div className="uvCompletedBox">
                        <h2>Verificación facial completada</h2>
                        <p>{message || "Este enlace ya fue utilizado correctamente. Puede regresar con el asesor."}</p>
                    </div>
                )}

                {done && !locked && (
                    <div className="uvCompletedBox">
                        <h2>Validación enviada</h2>
                        <p>{message || "La validación fue enviada correctamente. Puede regresar con el asesor."}</p>
                    </div>
                )}

                {!done && !locked && (
                    <>
                        <div className="uvSteps">
                            <button className={step === "front" ? "active" : front ? "done" : ""}>1. DNI frontal</button>
                            <button className={step === "back" ? "active" : back ? "done" : ""}>2. DNI posterior</button>
                            <button className={step === "face" ? "active" : faceVideo ? "done" : ""}>3. Verificación facial</button>
                        </div>

                        <div className="uvCameraBox">
                            <video ref={videoRef} playsInline muted className="uvVideo" />
                            {step !== "face" && <div className="uvDocFrame" />}
                            {step === "face" && <div className="uvFaceFrame" />}
                            {recording && <div className="uvCountdown">{countdown}</div>}
                        </div>

                        <div className="uvActions">
                            <button type="button" onClick={switchCamera} className="uvGhost">Cambiar cámara</button>
                            {step === "front" || step === "back" ? (
                                <button type="button" onClick={capturePhoto} className="uvPrimary" disabled={!cameraReady}>Tomar foto</button>
                            ) : !faceVideo ? (
                                <button type="button" onClick={startFaceRecording} className="uvPrimary" disabled={!cameraReady || recording}>
                                    {recording ? `Grabando ${countdown}s` : "Grabar rostro 5 segundos"}
                                </button>
                            ) : (
                                <button type="button" onClick={submitValidation} className="uvPrimary" disabled={loading}>
                                    {loading ? "Enviando..." : "Enviar validación"}
                                </button>
                            )}
                        </div>

                        <div className="uvPreviewGrid">
                            <div className={front ? "ready" : ""}><b>DNI frontal</b>{front && <img src={front} alt="DNI frontal" />}</div>
                            <div className={back ? "ready" : ""}><b>DNI posterior</b>{back && <img src={back} alt="DNI posterior" />}</div>
                            <div className={faceVideo ? "ready" : ""}><b>Video facial</b>{faceVideo && <span>Grabado</span>}</div>
                        </div>
                    </>
                )}

                {message && !locked && !done && <div className="uvNotice">{message}</div>}
            </section>
        </main>
    );
}
